export interface PracticeTypeData {
  name: string;
  slug: string;
  title: string;
  metaDescription: string;
  heroTitle: string;
  heroSubtitle: string;
  description: string;
  specificRisks: {
    icon: string;
    title: string;
    description: string;
    statistic: string;
  }[];
  recommendedCoverage: {
    coverage: string;
    amount: string;
    importance: "essential" | "recommended" | "optional";
    description: string;
  }[];
  caseStudies: {
    title: string;
    description: string;
    damage: string;
    solution: string;
  }[];
  faqs: {
    question: string;
    answer: string;
  }[];
  priceRange: string;
  employeeRange: string;
  practiceCount: string;
}

export const practiceTypes: Record<string, PracticeTypeData> = {
  hausarzt: {
    name: "Hausarztpraxen",
    slug: "hausarzt",
    title: "Cyberversicherung für Hausarztpraxen - Patientendaten sicher schützen",
    metaDescription: "Spezialisierte Cyberversicherung für Hausarztpraxen ab 29€/Monat. Schutz vor Ransomware, Datenverlust und DSGVO-Bußgeldern. Jetzt unverbindlich beraten lassen.",
    heroTitle: "Cyberversicherung für Hausarztpraxen",
    heroSubtitle: "Umfassender Schutz für Ihre Patientendaten und Praxissoftware ab 29€/Monat",
    description: "Als Hausarztpraxis verwalten Sie täglich sensible Gesundheitsdaten von hunderten Patienten. Ein Cyberangriff kann nicht nur zu hohen Kosten führen, sondern auch das Vertrauen Ihrer Patienten gefährden. Unsere spezialisierte Cyberversicherung schützt Ihre Praxis umfassend.",
    specificRisks: [
      {
        icon: "FileX",
        title: "Praxissoftware-Ausfall",
        description: "Ransomware verschlüsselt Ihre Praxissoftware. Patientenakten, Terminkalender und Abrechnungssysteme sind nicht mehr zugänglich.",
        statistic: "Ø 7 Tage Praxisstillstand"
      },
      {
        icon: "Database",
        title: "Patientendaten-Diebstahl",
        description: "Hacker stehlen Krankenakten, Diagnosen und Medikationspläne Ihrer Patienten. DSGVO-Bußgelder und Schadensersatzforderungen drohen.",
        statistic: "Bis zu 20 Mio. € Bußgeld"
      },
      {
        icon: "Mail",
        title: "Phishing-Angriffe",
        description: "Mitarbeiter öffnen präparierte E-Mails. Schadsoftware verbreitet sich im gesamten Praxisnetzwerk und kompromittiert alle Systeme.",
        statistic: "92% aller Cyberangriffe"
      },
      {
        icon: "Clock",
        title: "Betriebsunterbrechung",
        description: "Durch IT-Ausfall können keine Patienten behandelt werden. Umsatzverlust, fixe Kosten laufen weiter, Patienten weichen zu anderen Praxen aus.",
        statistic: "Ø 15.000€ Umsatzverlust/Woche"
      },
      {
        icon: "Shield",
        title: "Cyber-Erpressung",
        description: "Kriminelle drohen mit Veröffentlichung sensibler Patientendaten oder permanenter Datenlöschung und fordern Lösegeld.",
        statistic: "Ø 25.000€ Lösegeldforderung"
      },
      {
        icon: "AlertTriangle",
        title: "Datenschutzverletzungen",
        description: "Meldepflicht bei Datenpannen innerhalb 72h. Anwaltskosten, Behördenverfahren und Patienteninformationen verursachen hohe Folgekosten.",
        statistic: "Ø 8.500€ pro Vorfall"
      }
    ],
    recommendedCoverage: [
      {
        coverage: "Cyber-Eigenschaden",
        amount: "100.000 - 250.000 €",
        importance: "essential",
        description: "Datenwiederherstellung, Forensik, IT-Notfallkosten"
      },
      {
        coverage: "Betriebsunterbrechung",
        amount: "50.000 - 150.000 €",
        importance: "essential",
        description: "Umsatzausfall während IT-Ausfall, fixe Kosten"
      },
      {
        coverage: "Cyber-Haftpflicht",
        amount: "1.000.000 - 3.000.000 €",
        importance: "essential",
        description: "DSGVO-Bußgelder, Schadensersatz Dritter"
      },
      {
        coverage: "Cyber-Erpressung",
        amount: "25.000 - 100.000 €",
        importance: "recommended",
        description: "Lösegeldzahlungen, Verhandlungskosten"
      },
      {
        coverage: "Krisenmanagement",
        amount: "25.000 - 50.000 €",
        importance: "recommended",
        description: "PR-Beratung, Patientenkommunikation"
      },
      {
        coverage: "Rechtsschutz Cyber",
        amount: "50.000 - 100.000 €",
        importance: "optional",
        description: "Anwaltskosten bei Cyber-Rechtsstreitigkeiten"
      }
    ],
    caseStudies: [
      {
        title: "Hausarztpraxis München - Ransomware-Angriff",
        description: "Eine Hausarztpraxis mit 2.800 Patienten wurde durch Ransomware lahmgelegt. Alle Patientenakten waren verschlüsselt.",
        damage: "42.000 € Gesamtschaden (IT-Forensik, Datenrettung, 9 Tage Praxisausfall, Patienteninformation)",
        solution: "Die Cyberversicherung übernahm sämtliche Kosten inkl. Betriebsunterbrechung und stellte IT-Notfallexperten bereit."
      },
      {
        title: "Landpraxis Brandenburg - Phishing-Betrug",
        description: "Durch gefälschte E-Mail einer 'KV' wurde Schadsoftware installiert. Patientendaten wurden gestohlen.",
        damage: "28.500 € (DSGVO-Meldung, Anwaltskosten, Patientenbenachrichtigung, Datenschutzbeauftragter)",
        solution: "Versicherung deckte alle Kosten und organisierte Datenschutz-Schulungen für das Praxisteam."
      }
    ],
    faqs: [
      {
        question: "Was kostet eine Cyberversicherung für meine Hausarztpraxis?",
        answer: "Die Kosten hängen von Faktoren wie Praxisgröße, IT-Ausstattung und gewünschter Deckungssumme ab. Für eine typische Hausarztpraxis mit 2-5 Mitarbeitern liegen die Beiträge zwischen 29€ und 89€ pro Monat. Größere Praxen mit mehr Mitarbeitern zahlen entsprechend mehr."
      },
      {
        question: "Welche Deckungssumme benötige ich als Hausarzt?",
        answer: "Wir empfehlen mindestens 100.000€ für Eigenschäden und 1.000.000€ für Haftpflichtschäden. Bei mehr als 2.000 Patienten sollten die Summen auf 250.000€ bzw. 3.000.000€ erhöht werden, um auch bei schweren Datenpannen ausreichend geschützt zu sein."
      },
      {
        question: "Sind auch Schäden durch Mitarbeiterfehler versichert?",
        answer: "Ja, gute Cyberversicherungen decken auch fahrlässige Handlungen Ihrer Mitarbeiter ab, z.B. wenn versehentlich ein Phishing-Link angeklickt wird oder ein USB-Stick mit Schadsoftware verwendet wird."
      },
      {
        question: "Wie schnell greift die Versicherung im Schadensfall?",
        answer: "Bei einem Cyberangriff erhalten Sie innerhalb von 2-4 Stunden eine 24/7-Notfall-Hotline. IT-Forensiker und Datenrettungsexperten werden sofort aktiviert, um Ihre Praxis schnellstmöglich wieder arbeitsfähig zu machen."
      },
      {
        question: "Muss ich bestimmte IT-Sicherheitsmaßnahmen vorweisen?",
        answer: "Grundlegende Maßnahmen wie regelmäßige Backups, aktuelle Antivirensoftware und Firewalls werden vorausgesetzt. Die meisten Versicherer bieten aber auch Unterstützung bei der Implementierung dieser Maßnahmen an."
      },
      {
        question: "Sind auch Cloud-basierte Praxissysteme versichert?",
        answer: "Ja, moderne Cyberversicherungen decken sowohl lokale als auch cloudbasierte Systeme ab. Auch Schäden durch Ausfall von Cloud-Diensten oder Datenverlust in der Cloud sind versichert."
      }
    ],
    priceRange: "29-89",
    employeeRange: "2-8",
    practiceCount: "42.000+"
  },
  
  zahnarzt: {
    name: "Zahnarztpraxen",
    slug: "zahnarzt",
    title: "Cyberversicherung für Zahnarztpraxen - Röntgendaten & Abrechnungen schützen",
    metaDescription: "Spezialisierte Cyberversicherung für Zahnarztpraxen ab 35€/Monat. Schutz für digitale Röntgenbilder, Abrechnungssysteme und Patientendaten. Jetzt beraten lassen.",
    heroTitle: "Cyberversicherung für Zahnarztpraxen",
    heroSubtitle: "Spezieller Schutz für digitale Röntgensysteme und Abrechnungssoftware ab 35€/Monat",
    description: "Zahnarztpraxen arbeiten mit hochsensiblen digitalen Systemen: Röntgenbilder, 3D-Scans, digitale Abformungen und komplexe Abrechnungssoftware. Ein Cyberangriff kann den gesamten Praxisbetrieb zum Erliegen bringen und hohe Kosten verursachen.",
    specificRisks: [
      {
        icon: "FileImage",
        title: "Digitale Röntgendaten-Verlust",
        description: "Ransomware verschlüsselt Röntgenbilder, DVT-Aufnahmen und digitale Abformungen. Behandlungen können nicht fortgesetzt werden.",
        statistic: "Ø 12.000€ Wiederherstellungskosten"
      },
      {
        icon: "CreditCard",
        title: "Abrechnungssystem-Ausfall",
        description: "Ihr Abrechnungssystem für Privat- und Kassenpatienten fällt aus. Honorarabrechnungen verzögern sich, Cashflow-Probleme entstehen.",
        statistic: "Ø 25.000€ Umsatzverlust/Monat"
      },
      {
        icon: "Server",
        title: "Praxissoftware-Kompromittierung",
        description: "Behandlungspläne, Heil- und Kostenpläne sowie Patientenakten werden durch Schadsoftware beschädigt oder gestohlen.",
        statistic: "78% höheres Risiko als Hausarztpraxen"
      },
      {
        icon: "Wifi",
        title: "Netzwerk-Angriffe auf Geräte",
        description: "Moderne Zahnarztpraxen mit vernetzten Geräten (Röntgen, CAD/CAM, Intraoralscanner) bieten mehr Angriffsfläche für Hacker.",
        statistic: "Ø 8 vernetzte Geräte pro Praxis"
      },
      {
        icon: "Euro",
        title: "Zahlungsverkehr-Manipulation",
        description: "Cyberkriminelle manipulieren Überweisungen oder stehlen Zahlungsdaten von Privatpatienten beim Online-Banking.",
        statistic: "Ø 18.000€ Schaden"
      },
      {
        icon: "UserX",
        title: "Labor-Kommunikation gestört",
        description: "Verschlüsselte oder gehackte E-Mail-Systeme unterbrechen die digitale Kommunikation mit Dentallaboren.",
        statistic: "Ø 5 Tage Behandlungsverzögerung"
      }
    ],
    recommendedCoverage: [
      {
        coverage: "Cyber-Eigenschaden",
        amount: "150.000 - 300.000 €",
        importance: "essential",
        description: "Speziell für teure Röntgen- und CAD/CAM-Systeme"
      },
      {
        coverage: "Betriebsunterbrechung",
        amount: "75.000 - 200.000 €",
        importance: "essential",
        description: "Höhere Umsätze als Hausarztpraxen berücksichtigen"
      },
      {
        coverage: "Cyber-Haftpflicht",
        amount: "1.500.000 - 5.000.000 €",
        importance: "essential",
        description: "Schutz vor hohen DSGVO-Strafen bei Röntgendaten"
      },
      {
        coverage: "Datenträger-Wiederherstellung",
        amount: "50.000 - 100.000 €",
        importance: "recommended",
        description: "Speziell für digitale Bilddaten und 3D-Scans"
      },
      {
        coverage: "Cyber-Erpressung",
        amount: "50.000 - 150.000 €",
        importance: "recommended",
        description: "Zahnarztpraxen oft Ziel wegen hoher Umsätze"
      },
      {
        coverage: "Vertragsstrafen",
        amount: "25.000 - 75.000 €",
        importance: "optional",
        description: "Bei Verzug von Zahntechnik-Aufträgen"
      }
    ],
    caseStudies: [
      {
        title: "Zahnarztpraxis Hamburg - DVT-System gehackt",
        description: "Ransomware verschlüsselte sämtliche 3D-Röntgenaufnahmen eines Implantologie-Schwerpunkts. 47 laufende Behandlungen betroffen.",
        damage: "68.000 € (IT-Forensik, Datenrettung, 14 Tage Teilausfall, Neuplanung Implantate)",
        solution: "Versicherung stellte spezialisierte Forensiker für medizinische Bildsysteme und übernahm Behandlungsverzögerungskosten."
      },
      {
        title: "Gemeinschaftspraxis Berlin - Abrechnungs-Hack",
        description: "Hacker manipulierten das Abrechnungssystem und leiteten Privathonorar-Überweisungen um.",
        damage: "35.000 € (gestohlene Zahlungen, Forensik, neue Sicherheitssysteme, Patienteninformation)",
        solution: "Cyberversicherung erstattete gestohlene Beträge und finanzierte IT-Sicherheitsupgrade."
      }
    ],
    faqs: [
      {
        question: "Warum brauchen Zahnarztpraxen eine spezielle Cyberversicherung?",
        answer: "Zahnarztpraxen arbeiten mit besonders sensiblen und wertvollen digitalen Daten: Röntgenbilder, 3D-Scans, CAD/CAM-Daten und haben oft höhere Umsätze. Zudem sind mehr vernetzte Geräte im Einsatz, was mehr Angriffsfläche bietet. Eine Standard-Cyberversicherung deckt diese speziellen Risiken oft nicht ausreichend ab."
      },
      {
        question: "Was kostet eine Cyberversicherung für Zahnarztpraxen?",
        answer: "Für eine Einzelpraxis mit 3-6 Mitarbeitern liegen die Beiträge zwischen 35€ und 120€ monatlich. Größere Gemeinschaftspraxen oder Praxen mit Implantologie-Schwerpunkt (höhere Umsätze, mehr digitale Systeme) zahlen zwischen 150€ und 300€ pro Monat."
      },
      {
        question: "Sind auch externe Dentallabore mitversichert?",
        answer: "Die Versicherung deckt Schäden durch Datenverlust oder -diebstahl in der Kommunikation mit Laboren ab. Allerdings sind die Labore selbst nicht mitversichert - diese benötigen eine eigene Cyberversicherung."
      },
      {
        question: "Was passiert, wenn Röntgenbilder durch Cyberangriff verloren gehen?",
        answer: "Die Versicherung übernimmt die Kosten für Datenrettung durch spezialisierte Forensiker. Falls Daten nicht wiederherstellbar sind, werden auch Kosten für Neuaufnahmen und Behandlungsverzögerungen erstattet. Zudem unterstützt die Versicherung bei der Patientenkommunikation."
      },
      {
        question: "Wie hoch sollte die Deckungssumme für eine Zahnarztpraxis sein?",
        answer: "Wir empfehlen mindestens 150.000€ für Eigenschäden und 1.500.000€ für Haftpflicht. Bei Praxen mit DVT, CAD/CAM oder Intraoralscannern sollten die Summen auf 300.000€ bzw. 5.000.000€ erhöht werden."
      },
      {
        question: "Sind auch Cloud-basierte Praxissysteme und Röntgenspeicher versichert?",
        answer: "Ja, moderne Cyberversicherungen decken sowohl lokale als auch cloudbasierte Speicherlösungen ab. Auch Schäden durch Ausfall von Cloud-Diensten oder Datenverlust bei externen Servern sind versichert."
      }
    ],
    priceRange: "35-120",
    employeeRange: "3-12",
    practiceCount: "28.000+"
  },
  
  mvz: {
    name: "Medizinische Versorgungszentren (MVZ)",
    slug: "mvz",
    title: "Cyberversicherung für MVZ - Umfassender Schutz für große Einrichtungen",
    metaDescription: "Spezialisierte Cyberversicherung für Medizinische Versorgungszentren ab 150€/Monat. Schutz für komplexe IT-Infrastrukturen, Mehrfachstandorte und große Patientendatenbanken.",
    heroTitle: "Cyberversicherung für Medizinische Versorgungszentren",
    heroSubtitle: "Professioneller Schutz für komplexe IT-Infrastrukturen und Mehrfachstandorte ab 150€/Monat",
    description: "MVZ verwalten Patientendaten von tausenden Patienten über mehrere Standorte und Fachrichtungen hinweg. Die IT-Infrastruktur ist komplex, vernetzt und damit besonders anfällig für Cyberangriffe. Ein Ausfall kann katastrophale Folgen haben.",
    specificRisks: [
      {
        icon: "Building2",
        title: "Mehrfachstandort-Ausfall",
        description: "Ein Cyberangriff kann alle vernetzten Standorte gleichzeitig lahmlegen. Der Schaden potenziert sich durch die Größe der Einrichtung.",
        statistic: "Ø 250.000€ Schaden bei 5 Standorten"
      },
      {
        icon: "Database",
        title: "Große Patientendatenbanken",
        description: "MVZ verwalten oft 10.000+ Patientenakten digital. Ein Datenleck führt zu massiven DSGVO-Bußgeldern und Schadensersatzforderungen.",
        statistic: "Bis zu 4% des Jahresumsatzes Bußgeld"
      },
      {
        icon: "Network",
        title: "Komplexe Netzwerk-Infrastruktur",
        description: "Vernetzte Standorte, zentrale Server, Cloud-Systeme und mobile Endgeräte bieten viele Einfallstore für Cyberkriminelle.",
        statistic: "3x höheres Angriffsrisiko als Einzelpraxen"
      },
      {
        icon: "Users",
        title: "Viele Nutzer-Accounts",
        description: "Mit 20+ Mitarbeitern steigt das Risiko durch Phishing, schwache Passwörter oder Social Engineering exponentiell.",
        statistic: "85% Angriffe über Mitarbeiter-Accounts"
      },
      {
        icon: "Server",
        title: "Zentrale Server-Abhängigkeit",
        description: "Ausfall des zentralen Servers bedeutet Stillstand aller Standorte. Backup-Systeme werden oft mitangegriffen.",
        statistic: "Ø 21 Tage bis zur Wiederherstellung"
      },
      {
        icon: "FileWarning",
        title: "Regulatorische Anforderungen",
        description: "MVZ unterliegen strengeren Auflagen als Einzelpraxen. Datenpannen führen zu behördlichen Untersuchungen und höheren Strafen.",
        statistic: "Ø 125.000€ behördliche Verfahrenskosten"
      }
    ],
    recommendedCoverage: [
      {
        coverage: "Cyber-Eigenschaden",
        amount: "500.000 - 2.000.000 €",
        importance: "essential",
        description: "Für große IT-Infrastruktur und Mehrfachstandorte"
      },
      {
        coverage: "Betriebsunterbrechung",
        amount: "300.000 - 1.000.000 €",
        importance: "essential",
        description: "Hohe Fixkosten und Umsatzausfälle mehrerer Standorte"
      },
      {
        coverage: "Cyber-Haftpflicht",
        amount: "5.000.000 - 10.000.000 €",
        importance: "essential",
        description: "Schutz vor massiven DSGVO-Strafen bei großen Datenbanken"
      },
      {
        coverage: "Cyber-Erpressung",
        amount: "100.000 - 500.000 €",
        importance: "essential",
        description: "MVZ sind beliebte Ziele wegen hoher Zahlungsfähigkeit"
      },
      {
        coverage: "Krisenmanagement & PR",
        amount: "100.000 - 250.000 €",
        importance: "recommended",
        description: "Reputationsschäden bei großen Einrichtungen abfedern"
      },
      {
        coverage: "Rechtsschutz Cyber",
        amount: "150.000 - 300.000 €",
        importance: "recommended",
        description: "Komplexe rechtliche Auseinandersetzungen"
      }
    ],
    caseStudies: [
      {
        title: "MVZ Rhein-Main - Ransomware auf 7 Standorte",
        description: "Ransomware verbreitete sich über das zentrale Netzwerk auf alle 7 Standorte. 18.000 Patientenakten verschlüsselt, alle Fachrichtungen betroffen.",
        damage: "385.000 € (IT-Forensik, Datenrettung, 18 Tage Teilausfall, DSGVO-Meldung, Patienteninformation, Krisenmanagement)",
        solution: "Cyberversicherung koordinierte Notfall-Task-Force mit IT-Forensikern, übernahm alle Kosten inkl. Betriebsunterbrechung für 7 Standorte."
      },
      {
        title: "MVZ Nord - Zentral-Server gehackt",
        description: "Hacker verschafften sich über VPN-Schwachstelle Zugang zum zentralen Server. Patientendaten von 12.500 Patienten gestohlen.",
        damage: "520.000 € (Forensik, Behördenverfahren, DSGVO-Bußgeld 180.000€, Anwaltskosten, neue IT-Sicherheit, PR-Krisenmanagement)",
        solution: "Versicherung deckte Bußgelder, stellte Datenschutz-Anwälte und PR-Experten bereit, finanzierte komplettes IT-Sicherheitsupgrade."
      }
    ],
    faqs: [
      {
        question: "Warum benötigen MVZ eine spezielle Cyberversicherung?",
        answer: "MVZ haben eine deutlich komplexere IT-Infrastruktur als Einzelpraxen, verwalten größere Datenmengen über mehrere Standorte und unterliegen strengeren regulatorischen Anforderungen. Standard-Cyberversicherungen für Arztpraxen decken diese besonderen Risiken nicht ausreichend ab."
      },
      {
        question: "Was kostet eine Cyberversicherung für ein MVZ?",
        answer: "Die Kosten hängen stark von Faktoren wie Anzahl der Standorte, Mitarbeiterzahl, IT-Infrastruktur und Patientenzahl ab. Kleine MVZ mit 2-3 Standorten zahlen ab 150€ monatlich, größere Einrichtungen mit 5+ Standorten und 50+ Mitarbeitern zwischen 400€ und 1.200€ pro Monat."
      },
      {
        question: "Wie hoch sollte die Deckungssumme für ein MVZ sein?",
        answer: "Wir empfehlen mindestens 500.000€ für Eigenschäden und 5.000.000€ für Haftpflichtschäden. Bei MVZ mit mehr als 10.000 Patienten oder 5+ Standorten sollten die Summen auf 2.000.000€ bzw. 10.000.000€ erhöht werden."
      },
      {
        question: "Sind alle Standorte automatisch mitversichert?",
        answer: "Ja, bei korrekter Deklaration sind alle Standorte des MVZ mitversichert. Wichtig ist, dass Sie bei Eröffnung neuer Standorte oder Übernahme anderer Praxen Ihre Versicherung umgehend informieren, damit der Schutz nahtlos greift."
      },
      {
        question: "Was passiert bei einem Angriff, der mehrere Standorte betrifft?",
        answer: "Die Versicherung aktiviert ein koordiniertes Notfall-Team aus IT-Forensikern, das parallel an allen betroffenen Standorten arbeitet. Die Betriebsunterbrechungsdeckung greift für alle Standorte gleichzeitig."
      },
      {
        question: "Werden auch externe Dienstleister (Abrechnungsservice, Cloud-Provider) abgedeckt?",
        answer: "Schäden durch Ausfall oder Sicherheitsvorfälle bei externen Dienstleistern sind versichert, sofern diese im Rahmen Ihres Praxisbetriebs genutzt werden. Die Dienstleister selbst benötigen jedoch eigene Versicherungen."
      }
    ],
    priceRange: "150-1200",
    employeeRange: "20-150",
    practiceCount: "3.200+"
  },
  
  klinik: {
    name: "Kliniken & Krankenhäuser",
    slug: "klinik",
    title: "Cyberversicherung für Kliniken - Maximaler Schutz für Krankenhäuser",
    metaDescription: "Enterprise-Cyberversicherung für Kliniken und Krankenhäuser. Schutz für kritische Infrastrukturen, Medizingeräte-Netzwerke und Patientendatenbanken. Individuelle Beratung.",
    heroTitle: "Cyberversicherung für Kliniken & Krankenhäuser",
    heroSubtitle: "Enterprise-Schutz für kritische medizinische Infrastrukturen - Individuelle Lösungen",
    description: "Krankenhäuser und Kliniken sind hochkomplexe Organisationen mit kritischer IT-Infrastruktur. Ein Cyberangriff kann lebensbedrohliche Situationen verursachen und Millionenschäden anrichten. Enterprise-Level-Cyberversicherungen bieten maximalen Schutz.",
    specificRisks: [
      {
        icon: "Activity",
        title: "Lebenserhaltende Systeme",
        description: "Cyberangriffe auf vernetzte Medizingeräte wie Beatmungsgeräte, Infusionspumpen oder OP-Roboter können Leben gefährden.",
        statistic: "67% der Kliniken bereits angegriffen"
      },
      {
        icon: "Database",
        title: "Massive Patientendatenbanken",
        description: "Hundertausende Patientenakten, Labordaten, Röntgenbilder und OP-Berichte sind hochattraktive Ziele für Cyberkriminelle.",
        statistic: "Ø 500.000+ Datensätze pro Klinik"
      },
      {
        icon: "AlertCircle",
        title: "Notaufnahme-Ausfall",
        description: "IT-Ausfall kann zur Schließung der Notaufnahme führen. Patienten müssen umgeleitet werden, im Notfall lebensbedrohlich.",
        statistic: "Ø 4,5 Mio. € Schaden bei 3-Tage-Ausfall"
      },
      {
        icon: "Network",
        title: "Kritische Infrastruktur",
        description: "Kliniken gelten als KRITIS. Cyberangriffe können zu behördlichen Untersuchungen, Auflagen und massiven Strafen führen.",
        statistic: "BSI-KRITIS-Auflagen verpflichtend"
      },
      {
        icon: "Building",
        title: "Gebäudetechnik-Angriffe",
        description: "Vernetzte Gebäudetechnik (Klima, Aufzüge, Zugang) kann durch Cyberangriffe sabotiert werden und den Klinikbetrieb stören.",
        statistic: "Ø 85.000€ Reparaturkosten"
      },
      {
        icon: "Shield",
        title: "Reputationsschäden",
        description: "Datenpannen in Kliniken führen zu massivem Vertrauensverlust, Patientenabwanderung und langfristigen Umsatzeinbußen.",
        statistic: "Ø -18% Patientenzahlen im Folgejahr"
      }
    ],
    recommendedCoverage: [
      {
        coverage: "Cyber-Eigenschaden",
        amount: "5.000.000 - 20.000.000 €",
        importance: "essential",
        description: "Für umfassende IT-Infrastruktur und Medizintechnik"
      },
      {
        coverage: "Betriebsunterbrechung",
        amount: "2.000.000 - 10.000.000 €",
        importance: "essential",
        description: "Massive Umsatzausfälle bei Notaufnahme-Schließung"
      },
      {
        coverage: "Cyber-Haftpflicht",
        amount: "10.000.000 - 50.000.000 €",
        importance: "essential",
        description: "KRITIS-Anforderungen, massive DSGVO-Risiken"
      },
      {
        coverage: "Cyber-Erpressung",
        amount: "500.000 - 5.000.000 €",
        importance: "essential",
        description: "Kliniken sind Hauptziele für Ransomware-Angriffe"
      },
      {
        coverage: "Krisenmanagement & PR",
        amount: "250.000 - 1.000.000 €",
        importance: "essential",
        description: "Reputationsschutz bei öffentlichkeitswirksamen Vorfällen"
      },
      {
        coverage: "Medizingeräte-Wiederherstellung",
        amount: "500.000 - 2.000.000 €",
        importance: "recommended",
        description: "Spezialisierte Forensik für Medizintechnik"
      }
    ],
    caseStudies: [
      {
        title: "Universitätsklinik - Ransomware legt OP-Betrieb lahm",
        description: "Ransomware verschlüsselte das Kliniknetzwerk inkl. OP-Planungssystem. 28 geplante OPs mussten verschoben werden, Notaufnahme 48h geschlossen.",
        damage: "4,2 Mio. € (IT-Forensik, Systemwiederherstellung, Betriebsunterbrechung, Patientenverlegungen, Krisenmanagement, Behördenverfahren)",
        solution: "Enterprise-Cyberversicherung aktivierte 24/7-Task-Force mit Medizintechnik-Spezialisten, übernahm alle Kosten inkl. Patientenentschädigungen."
      },
      {
        title: "Kreiskrankenhaus - Patientendaten-Diebstahl",
        description: "Hacker stahlen über VPN-Schwachstelle 127.000 Patientendatensätze inkl. Diagnosen, OP-Berichten und Labordaten.",
        damage: "2,8 Mio. € (Forensik, DSGVO-Bußgeld 1,2 Mio.€, Anwaltskosten, Patienteninformation, IT-Sicherheitsupgrade, PR-Krise)",
        solution: "Versicherung deckte Bußgelder, stellte Datenschutz-Taskforce und Krisenmanagement-Team, finanzierte IT-Komplettsanierung."
      }
    ],
    faqs: [
      {
        question: "Warum benötigen Kliniken eine spezielle Enterprise-Cyberversicherung?",
        answer: "Kliniken sind kritische Infrastrukturen (KRITIS) mit hochkomplexen IT-Systemen, lebenserhaltender Medizintechnik und massiven Datenmengen. Die potenziellen Schäden gehen in die Millionen. Standard-Cyberversicherungen decken diese Größenordnung und Komplexität nicht ab."
      },
      {
        question: "Was kostet eine Cyberversicherung für Krankenhäuser?",
        answer: "Die Kosten werden individuell kalkuliert basierend auf Bettenzahl, IT-Infrastruktur, Fachbereichen und Risikoanalyse. Kleine Krankenhäuser (100-200 Betten) zahlen ab 1.500€ monatlich, große Universitätskliniken zwischen 8.000€ und 25.000€ pro Monat."
      },
      {
        question: "Welche Deckungssummen sind für Kliniken notwendig?",
        answer: "Wir empfehlen mindestens 5.000.000€ für Eigenschäden und 10.000.000€ für Haftpflicht. Große Kliniken (500+ Betten) oder Universitätskliniken sollten 20.000.000€ bzw. 50.000.000€ Deckung haben."
      },
      {
        question: "Sind auch Schäden an Medizingeräten durch Cyberangriffe versichert?",
        answer: "Ja, moderne Enterprise-Cyberversicherungen decken auch Schäden an vernetzter Medizintechnik ab. Dazu gehören Reparaturkosten, Datenwiederherstellung und Kosten für Ersatzgeräte während der Ausfallzeit."
      },
      {
        question: "Was passiert, wenn die Notaufnahme durch Cyberangriff geschlossen werden muss?",
        answer: "Die Betriebsunterbrechungsversicherung greift sofort. Alle Umsatzausfälle, Kosten für Patientenverlegungen und zusätzliche Maßnahmen werden erstattet. Zudem unterstützt die Versicherung bei der Koordination mit Behörden und anderen Kliniken."
      },
      {
        question: "Wie schnell reagiert die Versicherung im Notfall?",
        answer: "Enterprise-Cyberversicherungen bieten 24/7-Notfall-Hotlines mit maximal 1 Stunde Reaktionszeit. Ein spezialisiertes Task-Force-Team aus IT-Forensikern, Medizintechnik-Experten und Krisenmanagern wird sofort aktiviert."
      }
    ],
    priceRange: "1500-25000",
    employeeRange: "100-5000",
    practiceCount: "1.900+"
  },
  
  facharzt: {
    name: "Facharztpraxen",
    slug: "facharzt",
    title: "Cyberversicherung für Facharztpraxen - Spezialisierter Schutz",
    metaDescription: "Cyberversicherung für Fachärzte ab 32€/Monat. Spezieller Schutz für Dermatologen, Radiologen, Orthopäden, Gynäkologen, Kardiologen. Jetzt beraten lassen.",
    heroTitle: "Cyberversicherung für Facharztpraxen",
    heroSubtitle: "Spezialisierter Schutz für Fachrichtungen mit besonderen IT-Anforderungen ab 32€/Monat",
    description: "Facharztpraxen haben je nach Fachrichtung unterschiedliche IT-Anforderungen und Risiken: Radiologen mit großen Bilddatenbanken, Dermatologen mit Fotodokumentationen, Kardiologen mit Diagnosegeräten. Unsere Cyberversicherung berücksichtigt diese Besonderheiten.",
    specificRisks: [
      {
        icon: "Microscope",
        title: "Fachspezifische Datenbanken",
        description: "Je nach Fachrichtung: Große Bilddatenbanken (Radiologie, Dermatologie), EKG-Daten (Kardiologie), OP-Dokumentationen (Chirurgie).",
        statistic: "Ø 45GB Daten pro Facharztpraxis"
      },
      {
        icon: "Stethoscope",
        title: "Diagnosegeräte-Vernetzung",
        description: "Moderne Diagnosegeräte (MRT, CT, Ultraschall, EKG) sind vernetzt und können durch Cyberangriffe kompromittiert werden.",
        statistic: "72% der Geräte netzwerkfähig"
      },
      {
        icon: "FileText",
        title: "Spezialisierte Abrechnungssysteme",
        description: "Fachärzte nutzen oft spezialisierte Abrechnungssoftware mit höherer Komplexität. Ausfall führt zu massiven Verzögerungen.",
        statistic: "Ø 18.000€ Honorarverzug pro Woche"
      },
      {
        icon: "Image",
        title: "Bildgebende Verfahren",
        description: "Röntgen, MRT, CT, Ultraschall - große Datenmengen und hohe Anforderungen an Datensicherheit und Verfügbarkeit.",
        statistic: "Ø 2.500 Bilddateien pro Monat"
      },
      {
        icon: "Calendar",
        title: "Online-Terminbuchung",
        description: "Viele Facharztpraxen bieten Online-Terminbuchung. Hackerangriffe können Terminkalender manipulieren oder löschen.",
        statistic: "38% nutzen Online-Buchungssysteme"
      },
      {
        icon: "Users2",
        title: "Überweisungsnetzwerk",
        description: "Intensive digitale Kommunikation mit zuweisenden Ärzten, Kliniken und Laboren. Kompromittierte Kommunikation gefährdet Patientenversorgung.",
        statistic: "Ø 120 Überweisungen pro Woche"
      }
    ],
    recommendedCoverage: [
      {
        coverage: "Cyber-Eigenschaden",
        amount: "100.000 - 500.000 €",
        importance: "essential",
        description: "Je nach Fachrichtung und Geräteausstattung"
      },
      {
        coverage: "Betriebsunterbrechung",
        amount: "50.000 - 250.000 €",
        importance: "essential",
        description: "Höher bei bildgebenden Fachrichtungen"
      },
      {
        coverage: "Cyber-Haftpflicht",
        amount: "1.000.000 - 5.000.000 €",
        importance: "essential",
        description: "Große Datenmengen = höheres DSGVO-Risiko"
      },
      {
        coverage: "Bilddaten-Wiederherstellung",
        amount: "50.000 - 200.000 €",
        importance: "recommended",
        description: "Speziell für Radiologie, Dermatologie"
      },
      {
        coverage: "Cyber-Erpressung",
        amount: "25.000 - 150.000 €",
        importance: "recommended",
        description: "Fachärzte oft lukrative Ziele"
      },
      {
        coverage: "Geräte-Wiederinbetriebnahme",
        amount: "25.000 - 100.000 €",
        importance: "optional",
        description: "Für vernetzte Diagnosegeräte"
      }
    ],
    caseStudies: [
      {
        title: "Radiologiepraxis - MRT-System gehackt",
        description: "Ransomware verschlüsselte PACS-System mit 12.000 MRT/CT-Aufnahmen. Befundung unmöglich, Patienten mussten neu untersucht werden.",
        damage: "78.000 € (Forensik, Datenrettung, 11 Tage Ausfall, Neuuntersuchungen, Patientenentschädigungen)",
        solution: "Cyberversicherung stellte medizintechnische IT-Spezialisten, übernahm alle Kosten inkl. Neuuntersuchungen."
      },
      {
        title: "Dermatologie - Fotodatenbank gestohlen",
        description: "Hacker stahlen 8.500 Patientenfotos (Hautkrebsvorsorge, OP-Dokumentationen). Massive DSGVO-Verletzung.",
        damage: "52.000 € (Forensik, DSGVO-Meldung, Anwaltskosten, Patienteninformation, Krisenmanagement)",
        solution: "Versicherung deckte alle Kosten, stellte Datenschutz-Anwälte und PR-Berater für Krisenkommunikation."
      }
    ],
    faqs: [
      {
        question: "Unterscheidet sich die Cyberversicherung je nach Fachrichtung?",
        answer: "Ja, die Anforderungen variieren stark: Radiologen benötigen höhere Deckungssummen für Bilddaten, Kardiologen für Diagnosegeräte, Chirurgen für OP-Dokumentationen. Wir passen die Versicherung individuell an Ihre Fachrichtung an."
      },
      {
        question: "Was kostet eine Cyberversicherung für Fachärzte?",
        answer: "Die Kosten hängen von der Fachrichtung ab. Internisten oder Allgemeinmediziner zahlen ab 32€ monatlich, bildgebende Fachrichtungen (Radiologie, Kardiologie) zwischen 85€ und 250€ pro Monat wegen höherer Datenmengen und teurerer Geräte."
      },
      {
        question: "Sind auch externe Befundungsplattformen versichert?",
        answer: "Ja, Schäden durch Ausfall oder Sicherheitsvorfälle bei externen Befundungsplattformen oder Teleradiologie-Diensten sind versichert, sofern diese im Rahmen Ihrer Praxistätigkeit genutzt werden."
      },
      {
        question: "Was passiert, wenn bildgebende Geräte durch Cyberangriff ausfallen?",
        answer: "Die Versicherung übernimmt Kosten für IT-Forensik an Medizingeräten, Wiederinbetriebnahme, Datenrettung und bei Bedarf Mietkosten für Ersatzgeräte während der Ausfallzeit."
      },
      {
        question: "Wie hoch sollte die Deckungssumme für meine Fachrichtung sein?",
        answer: "Das hängt von Ihrer Fachrichtung ab: Internisten/Gynäkologen 100.000-150.000€, Orthopäden/Dermatologen 150.000-250.000€, Radiologen/Kardiologen 250.000-500.000€ für Eigenschäden. Haftpflicht mindestens 1.000.000€, bei bildgebenden Verfahren 3.000.000-5.000.000€."
      },
      {
        question: "Werden Schulungen für Mitarbeiter von der Versicherung unterstützt?",
        answer: "Viele Versicherer bieten im Rahmen der Cyberversicherung kostenlose Cyber-Security-Awareness-Schulungen für Praxisteams an, um Risiken durch Phishing und Social Engineering zu minimieren."
      }
    ],
    priceRange: "32-250",
    employeeRange: "2-15",
    practiceCount: "35.000+"
  },
  
  physiotherapie: {
    name: "Physiotherapie-Praxen",
    slug: "physiotherapie",
    title: "Cyberversicherung für Physiotherapie-Praxen - Patientendaten schützen",
    metaDescription: "Spezielle Cyberversicherung für Physiotherapeuten ab 24€/Monat. Schutz vor Datenverlust, Ransomware und DSGVO-Strafen. Jetzt beraten lassen.",
    heroTitle: "Cyberversicherung für Physiotherapie",
    heroSubtitle: "Digitale Absicherung für Ihre Praxis ab 24€/Monat",
    description: "Physiotherapie-Praxen verwalten umfangreiche Patientendaten, Behandlungspläne und Abrechnungen digital. Ein Cyberangriff kann Ihre Praxis lahmlegen und DSGVO-Bußgelder verursachen.",
    specificRisks: [
      { icon: "FileX", title: "Praxissoftware-Ausfall", description: "Ransomware verschlüsselt Behandlungspläne und Terminkalender.", statistic: "Ø 5 Tage Ausfall" },
      { icon: "Database", title: "Patientendaten-Verlust", description: "Therapieverläufe und Diagnosen gehen verloren oder werden gestohlen.", statistic: "Bis zu 50.000€ Bußgeld" },
      { icon: "Clock", title: "Terminausfall", description: "Online-Terminbuchung und Verwaltungssystem nicht verfügbar.", statistic: "Ø 8.000€ Umsatzausfall" }
    ],
    recommendedCoverage: [
      { coverage: "Cyber-Eigenschaden", amount: "50.000 - 100.000 €", importance: "essential", description: "Datenwiederherstellung, IT-Notfall" },
      { coverage: "Betriebsunterbrechung", amount: "25.000 - 75.000 €", importance: "recommended", description: "Umsatzausfall bei IT-Ausfall" },
      { coverage: "Cyber-Haftpflicht", amount: "500.000 - 1.000.000 €", importance: "essential", description: "DSGVO-Bußgelder, Schadensersatz" }
    ],
    caseStudies: [
      { title: "Ransomware-Angriff", description: "Physio-Praxis mit 3 Therapeuten erlitt Ransomware-Angriff", damage: "18.500 €", solution: "Versicherung übernahm IT-Forensik, Datenwiederherstellung und 4 Tage Betriebsunterbrechung" }
    ],
    faqs: [
      { question: "Brauchen Physio-Praxen eine Cyberversicherung?", answer: "Ja, da Sie sensible Gesundheitsdaten verarbeiten. DSGVO gilt auch für Therapeuten. Bei Datenpannen drohen Bußgelder bis 50.000€." },
      { question: "Was kostet Cyberversicherung für Physiotherapeuten?", answer: "Ab 24€ monatlich für Einzelpraxen. Größere Praxen mit mehreren Therapeuten zahlen 40-85€/Monat." }
    ],
    priceRange: "24-85",
    employeeRange: "1-8",
    practiceCount: "42.000+"
  },
  
  apotheke: {
    name: "Apotheken",
    slug: "apotheke",
    title: "Cyberversicherung für Apotheken - E-Rezept & Warenwirtschaft absichern",
    metaDescription: "Cyberversicherung für Apotheken ab 35€/Monat. Schutz vor Ransomware auf Warenwirtschaft, E-Rezept-Ausfall und Datenpannen. Jetzt Angebot einholen.",
    heroTitle: "Cyberversicherung für Apotheken",
    heroSubtitle: "Rundum-Schutz für Warenwirtschaft und E-Rezept ab 35€/Monat",
    description: "Apotheken sind hochgradig digitalisiert: Warenwirtschaftssysteme, E-Rezept-Anbindung, Kundendatenbanken. Ein Cyberangriff kann den gesamten Betrieb lahmlegen und hohe finanzielle Schäden verursachen.",
    specificRisks: [
      { icon: "Package", title: "Warenwirtschafts-Ausfall", description: "Ransomware blockiert Bestellsystem, Lagerbestand und Kassensystem.", statistic: "Ø 12.000€/Tag Umsatzverlust" },
      { icon: "Pill", title: "E-Rezept-System-Störung", description: "E-Rezept-Anbindung fällt aus, Kunden können Medikamente nicht abholen.", statistic: "60% Umsatzeinbruch" },
      { icon: "ShieldAlert", title: "Kundendaten-Diebstahl", description: "Medikationshistorien, Rezepte und Kundendaten werden gestohlen.", statistic: "Bis zu 100.000€ Bußgeld" },
      { icon: "Network", title: "TI-Ausfall", description: "Telematikinfrastruktur-Angriff verhindert E-Rezept und Kassenabruf.", statistic: "Kompletter Betriebsstillstand" }
    ],
    recommendedCoverage: [
      { coverage: "Cyber-Eigenschaden", amount: "150.000 - 300.000 €", importance: "essential", description: "Warenwirtschaft-Wiederherstellung" },
      { coverage: "Betriebsunterbrechung", amount: "100.000 - 250.000 €", importance: "essential", description: "Täglicher Umsatzausfall sehr hoch" },
      { coverage: "Cyber-Haftpflicht", amount: "1.500.000 - 3.000.000 €", importance: "essential", description: "DSGVO-Bußgelder, Kundenschadensersatz" }
    ],
    caseStudies: [
      { title: "Warenwirtschafts-Ransomware", description: "Stadtapotheke mit 8 Mitarbeitern - Komplett-Ausfall für 6 Tage", damage: "87.000 €", solution: "Versicherung zahlte: IT-Forensik (12.000€), Datenwiederherstellung (18.000€), Betriebsunterbrechung (52.000€), Notfall-IT (5.000€)" }
    ],
    faqs: [
      { question: "Ist E-Rezept-Ausfall versichert?", answer: "Ja, Betriebsunterbrechung durch E-Rezept-Systemausfälle ist mitversichert, ebenso wie TI-Störungen." },
      { question: "Was kostet Cyberversicherung für Apotheken?", answer: "35-95€/Monat für Einzelapotheken, 120-280€/Monat für Apotheken mit Filialstruktur oder hohem Umsatz." },
      { question: "Sind Warenwirtschaftssysteme abgedeckt?", answer: "Ja, Schäden an und durch Warenwirtschaftssysteme (Bestellwesen, Lager, Kasse) sind vollumfänglich versichert." }
    ],
    priceRange: "35-280",
    employeeRange: "3-20",
    practiceCount: "18.500+"
  },
  
  tierarzt: {
    name: "Tierarztpraxen",
    slug: "tierarzt",
    title: "Cyberversicherung für Tierarztpraxen - Praxissoftware schützen",
    metaDescription: "Spezielle Cyberversicherung für Tierärzte ab 28€/Monat. Schutz vor Ransomware, Datenverlust und IT-Ausfällen. Beratung anfordern.",
    heroTitle: "Cyberversicherung für Tierarztpraxen",
    heroSubtitle: "Digitaler Schutz für Ihre Praxis ab 28€/Monat",
    description: "Tierarztpraxen und Tierkliniken verarbeiten umfangreiche Patientendaten, Röntgenbilder und Abrechnungen digital. Cyber-Risiken sind real und können existenzbedrohend sein.",
    specificRisks: [
      { icon: "FileX", title: "Praxissoftware-Verschlüsselung", description: "Tierpatientenakten, Impfpläne und OP-Berichte werden verschlüsselt.", statistic: "Ø 6 Tage Stillstand" },
      { icon: "Image", title: "Röntgen-/Ultraschallbild-Verlust", description: "Diagnostische Bilder gehen verloren oder werden unzugänglich.", statistic: "Neuaufnahmen oft unmöglich" },
      { icon: "Clock", title: "Notdienst-Ausfall", description: "Bei IT-Ausfall im Notdienst keine Patientenhistorie verfügbar.", statistic: "Behandlungsrisiko steigt" }
    ],
    recommendedCoverage: [
      { coverage: "Cyber-Eigenschaden", amount: "75.000 - 150.000 €", importance: "essential", description: "Datenwiederherstellung, IT-Notfall" },
      { coverage: "Betriebsunterbrechung", amount: "50.000 - 100.000 €", importance: "recommended", description: "Umsatzausfall während IT-Ausfall" },
      { coverage: "Cyber-Haftpflicht", amount: "500.000 - 1.500.000 €", importance: "essential", description: "Datenschutz-Bußgelder" }
    ],
    caseStudies: [
      { title: "Ransomware-Angriff Tierklinik", description: "Tierklinik mit Notdienst - 4 Tage Totalausfall", damage: "42.000 €", solution: "Versicherung deckte: Forensik, Datenrettung, Betriebsunterbrechung, Notfall-Papier-System" }
    ],
    faqs: [
      { question: "Brauchen Tierärzte Cyberversicherung?", answer: "Ja! Auch Tierdaten unterliegen Datenschutz. Bei Datenpannen drohen Bußgelder. Zudem ist der Betrieb ohne IT kaum noch möglich." },
      { question: "Was kostet Versicherung für Tierarztpraxen?", answer: "28-75€/Monat für Einzelpraxen, 95-180€/Monat für Tierkliniken mit OP und Stationär-Bereich." }
    ],
    priceRange: "28-180",
    employeeRange: "2-12",
    practiceCount: "12.000+"
  },
  
  hebamme: {
    name: "Hebammenpraxen",
    slug: "hebamme",
    title: "Cyberversicherung für Hebammen - Geburtsdaten sicher schützen",
    metaDescription: "Cyberversicherung für Hebammen & Geburtshäuser ab 22€/Monat. DSGVO-konformer Schutz für sensible Geburtsdaten. Jetzt informieren.",
    heroTitle: "Cyberversicherung für Hebammen",
    heroSubtitle: "Schutz für Geburtsdokumentation und Patientendaten ab 22€/Monat",
    description: "Hebammen dokumentieren hochsensible Daten rund um Schwangerschaft und Geburt digital. Ein Datenverlust oder Cyberangriff kann rechtliche und finanzielle Folgen haben.",
    specificRisks: [
      { icon: "Baby", title: "Geburtsdokumentation-Verlust", description: "Schwangerschaftsverläufe, Geburtsberichte und Nachsorgedokumente gehen verloren.", statistic: "Rechtliche Konsequenzen" },
      { icon: "FileX", title: "Dokumentationssoftware-Ausfall", description: "Hebammen-Software wird verschlüsselt, keine Dokumentation mehr möglich.", statistic: "DSGVO-Meldefrist 72h" },
      { icon: "Shield", title: "Datenschutzverletzung", description: "Besonders sensible Gesundheitsdaten (Geburt) werden kompromittiert.", statistic: "Bis zu 75.000€ Bußgeld" }
    ],
    recommendedCoverage: [
      { coverage: "Cyber-Eigenschaden", amount: "30.000 - 75.000 €", importance: "essential", description: "Datenwiederherstellung" },
      { coverage: "Cyber-Haftpflicht", amount: "500.000 - 1.000.000 €", importance: "essential", description: "DSGVO-Bußgelder, Schadensersatz" },
      { coverage: "Rechtsschutz Cyber", amount: "50.000 €", importance: "recommended", description: "Anwaltskosten bei Datenpannen" }
    ],
    caseStudies: [
      { title: "Datenverlust durch Hardware-Defekt", description: "Freiberufliche Hebamme verlor 2 Jahre Geburtsdokumentation", damage: "8.500 €", solution: "Versicherung zahlte: Datenrettung (3.500€), DSGVO-Meldung (2.000€), Anwaltskosten (3.000€)" }
    ],
    faqs: [
      { question: "Müssen Hebammen Cyberversicherung haben?", answer: "Nicht verpflichtend, aber dringend empfohlen. Geburtsdaten sind besonders schützenswert (DSGVO Art. 9). Bei Datenpannen drohen hohe Strafen." },
      { question: "Was kostet Cyberversicherung für Hebammen?", answer: "22-45€/Monat für freiberufliche Hebammen, 55-85€/Monat für Geburtshäuser mit mehreren Hebammen." }
    ],
    priceRange: "22-85",
    employeeRange: "1-5",
    practiceCount: "8.000+"
  },
  
  psychotherapie: {
    name: "Psychotherapie-Praxen",
    slug: "psychotherapie",
    title: "Cyberversicherung für Psychotherapeuten - Therapiedaten schützen",
    metaDescription: "Spezialisierte Cyberversicherung für Psychotherapeuten ab 26€/Monat. Höchster Schutz für hochsensible Therapiedaten. Beratung anfordern.",
    heroTitle: "Cyberversicherung für Psychotherapeuten",
    heroSubtitle: "Maximaler Schutz für Therapiedokumentation ab 26€/Monat",
    description: "Psychotherapie-Praxen verarbeiten besonders sensible Daten: Diagnosen, Therapieverläufe, persönliche Krisen. Der Schutz dieser Daten hat höchste Priorität.",
    specificRisks: [
      { icon: "Brain", title: "Therapiedaten-Diebstahl", description: "Diagnosen, Sitzungsnotizen und persönliche Krisen werden gestohlen.", statistic: "Existenzbedrohend" },
      { icon: "Lock", title: "Besonders sensible Daten", description: "Psychische Erkrankungen fallen unter Art. 9 DSGVO - höchste Schutzstufe.", statistic: "Bis zu 200.000€ Bußgeld" },
      { icon: "FileX", title: "Praxissoftware-Verschlüsselung", description: "Therapeutensoftware wird von Ransomware befallen.", statistic: "Behandlung unmöglich" }
    ],
    recommendedCoverage: [
      { coverage: "Cyber-Eigenschaden", amount: "75.000 - 150.000 €", importance: "essential", description: "IT-Forensik, Datenwiederherstellung" },
      { coverage: "Cyber-Haftpflicht", amount: "1.000.000 - 2.000.000 €", importance: "essential", description: "DSGVO-Bußgelder bei sensiblen Daten" },
      { coverage: "Krisenmanagement", amount: "50.000 €", importance: "recommended", description: "PR, Patientenkommunikation" }
    ],
    caseStudies: [
      { title: "Phishing-Angriff Psychotherapie-Praxis", description: "Praxis mit 2 Therapeuten - Therapiedaten kompromittiert", damage: "65.000 €", solution: "Versicherung übernahm: IT-Forensik (8.000€), DSGVO-Bußgeld (35.000€), Anwaltskosten (12.000€), Krisenmanagement (10.000€)" }
    ],
    faqs: [
      { question: "Warum brauchen Psychotherapeuten spezielle Cyberversicherung?", answer: "Therapiedaten sind besonders schützenswert (DSGVO Art. 9). Bußgelder fallen bei Psycho-Daten deutlich höher aus. Reputationsschaden bei Datenlecks ist gravierend." },
      { question: "Was kostet Cyberversicherung für Therapeuten?", answer: "26-65€/Monat für Einzelpraxen, 75-140€/Monat für Gemeinschaftspraxen." }
    ],
    priceRange: "26-140",
    employeeRange: "1-6",
    practiceCount: "25.000+"
  },
  
  radiologie: {
    name: "Radiologie-Praxen",
    slug: "radiologie",
    title: "Cyberversicherung für Radiologen - PACS & Bilddaten absichern",
    metaDescription: "Hochspezialisierte Cyberversicherung für Radiologen ab 120€/Monat. Schutz für PACS, CT, MRT und Terabytes an Bilddaten. Experten-Beratung.",
    heroTitle: "Cyberversicherung für Radiologen",
    heroSubtitle: "Spezialisierter Schutz für PACS und medizinische Bildgebung ab 120€/Monat",
    description: "Radiologie-Praxen verarbeiten Terabytes an hochsensiblen Bilddaten (CT, MRT, Röntgen). PACS-Systeme sind kritische Infrastruktur - Ausfälle sind existenzbedrohend.",
    specificRisks: [
      { icon: "HardDrive", title: "PACS-System-Ransomware", description: "Picture Archiving System wird verschlüsselt. Jahre an diagnostischen Bildern unzugänglich.", statistic: "Ø 500.000€ Schaden" },
      { icon: "Scan", title: "Modalitäten-Ausfall", description: "CT, MRT, Röntgengeräte können Bilder nicht mehr an PACS senden.", statistic: "Kompletter Betriebsstillstand" },
      { icon: "Database", title: "Bilddaten-Diebstahl", description: "Millionen hochauflösende medizinische Bilder werden gestohlen.", statistic: "Bis zu 5 Mio. € Bußgeld" },
      { icon: "Network", title: "Teleradiologie-Ausfall", description: "Befundung über Fernzugriff nicht mehr möglich.", statistic: "Hoher Umsatzverlust" }
    ],
    recommendedCoverage: [
      { coverage: "Cyber-Eigenschaden", amount: "500.000 - 1.500.000 €", importance: "essential", description: "PACS-Wiederherstellung, Terabytes Daten" },
      { coverage: "Betriebsunterbrechung", amount: "250.000 - 750.000 €", importance: "essential", description: "Hohe Tagesumsätze" },
      { coverage: "Cyber-Haftpflicht", amount: "3.000.000 - 10.000.000 €", importance: "essential", description: "Massive DSGVO-Risiken" }
    ],
    caseStudies: [
      { title: "PACS-Ransomware Radiologiezentrum", description: "Großes Radiologiezentrum - 8 Tage Totalausfall", damage: "780.000 €", solution: "Versicherung deckte: Forensik (45.000€), PACS-Wiederherstellung (180.000€), Betriebsunterbrechung (480.000€), Ersatz-Bildgebung extern (75.000€)" }
    ],
    faqs: [
      { question: "Warum sind Radiologen besonders gefährdet?", answer: "Riesige Datenmengen (Terabytes), hochkomplexe PACS-Systeme, vernetzte Modalitäten. Ransomware-Gruppen wissen: Ohne PACS ist Praxis wertlos." },
      { question: "Was kostet Cyberversicherung für Radiologen?", answer: "120-450€/Monat für kleinere Praxen, 500-1.500€/Monat für große Radiologiezentren mit Teleradiologie." }
    ],
    priceRange: "120-1500",
    employeeRange: "5-50",
    practiceCount: "2.500+"
  },
  
  labor: {
    name: "Medizinische Labore",
    slug: "labor",
    title: "Cyberversicherung für Labore - Befundsysteme & Analysegeräte schützen",
    metaDescription: "Cyberversicherung für medizinische Labore ab 95€/Monat. Schutz für Befundsysteme, Analysegeräte und Patientendaten. Jetzt Angebot anfragen.",
    heroTitle: "Cyberversicherung für Medizinische Labore",
    heroSubtitle: "Spezialisierter Schutz für Labor-IT ab 95€/Monat",
    description: "Labore verarbeiten Millionen sensibler Befunde. Vernetzte Analysegeräte, Labor-IT und Befundübermittlung sind kritisch. Cyber-Risiken können Betrieb komplett lahmlegen.",
    specificRisks: [
      { icon: "FlaskConical", title: "LIS-System-Ausfall", description: "Labor-Informationssystem wird verschlüsselt. Befund-Erstellung unmöglich.", statistic: "Ø 50.000€/Tag Verlust" },
      { icon: "Network", title: "Analysegeräte-Kompromittierung", description: "Vernetzte Geräte werden von Ransomware befallen.", statistic: "Wochenlanger Ausfall" },
      { icon: "Database", title: "Befunddaten-Diebstahl", description: "Millionen Laborbefunde (HIV, Krebs, genetisch) werden gestohlen.", statistic: "Bis zu 10 Mio. € Bußgeld" }
    ],
    recommendedCoverage: [
      { coverage: "Cyber-Eigenschaden", amount: "300.000 - 1.000.000 €", importance: "essential", description: "LIS-Wiederherstellung, Geräteschutz" },
      { coverage: "Betriebsunterbrechung", amount: "200.000 - 500.000 €", importance: "essential", description: "Hohe Tagesumsätze" },
      { coverage: "Cyber-Haftpflicht", amount: "2.000.000 - 5.000.000 €", importance: "essential", description: "Millionen Befunde, massive Haftung" }
    ],
    caseStudies: [
      { title: "Ransomware-Angriff Großlabor", description: "Labor mit 80 Mitarbeitern - 12 Tage Ausfall", damage: "920.000 €", solution: "Versicherung zahlte: IT-Forensik (65.000€), LIS-Neuaufbau (280.000€), Betriebsunterbrechung (520.000€), Fremd-Beauftragung (55.000€)" }
    ],
    faqs: [
      { question: "Sind auch Analysegeräte mitversichert?", answer: "Ja, vernetzte Laborgeräte und deren Cyber-Risiken sind mitversichert. Auch Firmware-Manipulationen und Ausfall durch Ransomware." },
      { question: "Was kostet Cyberversicherung für Labore?", answer: "95-350€/Monat für kleinere Speziallabore, 450-1.800€/Monat für Großlabore mit hohem Durchsatz." }
    ],
    priceRange: "95-1800",
    employeeRange: "8-100",
    practiceCount: "1.200+"
  },
  
  reha: {
    name: "Rehakliniken",
    slug: "reha",
    title: "Cyberversicherung für Rehakliniken - KIS & Patientendaten absichern",
    metaDescription: "Spezialisierte Cyberversicherung für Rehakliniken ab 180€/Monat. Schutz für KIS, Therapiesysteme und Langzeit-Patientendaten. Beratung.",
    heroTitle: "Cyberversicherung für Rehakliniken",
    heroSubtitle: "Umfassender Schutz für Krankenhausinformationssysteme ab 180€/Monat",
    description: "Rehakliniken betreuen Patienten über Wochen stationär. KIS-Systeme, Therapieplanung und medizinische Dokumentation sind hochkomplex und kritisch.",
    specificRisks: [
      { icon: "Building2", title: "KIS-Totalausfall", description: "Krankenhaus-Informationssystem wird verschlüsselt. Stationsbetrieb kollabiert.", statistic: "Ø 2-3 Wochen Ausfall" },
      { icon: "Users", title: "Stationäre Behandlung unmöglich", description: "Ohne IT keine Medikation, keine OP-Planung, keine Entlassungen.", statistic: "Existenzbedrohend" },
      { icon: "HeartPulse", title: "Therapiegeräte-Ausfall", description: "Vernetzte Reha-Geräte und Therapiesysteme betroffen.", statistic: "Behandlung eingestellt" },
      { icon: "Database", title: "Langzeit-Patientendaten-Verlust", description: "Jahre an Reha-Verläufen und Therapiedokumentationen verloren.", statistic: "Bis zu 20 Mio. € Bußgeld" }
    ],
    recommendedCoverage: [
      { coverage: "Cyber-Eigenschaden", amount: "1.000.000 - 5.000.000 €", importance: "essential", description: "KIS-Wiederherstellung, sehr komplex" },
      { coverage: "Betriebsunterbrechung", amount: "500.000 - 2.000.000 €", importance: "essential", description: "Stationsbetrieb, Fixkosten" },
      { coverage: "Cyber-Haftpflicht", amount: "5.000.000 - 20.000.000 €", importance: "essential", description: "Hunderte Patienten betroffen" }
    ],
    caseStudies: [
      { title: "KIS-Ransomware Rehaklinik", description: "Rehaklinik 120 Betten - 18 Tage Teil-Betrieb", damage: "2.400.000 €", solution: "Versicherung deckte: Forensik (95.000€), KIS-Notbetrieb (380.000€), Betriebsunterbrechung (1.750.000€), Patientenumleitungen (175.000€)" }
    ],
    faqs: [
      { question: "Brauchen Rehakliniken spezielle Cyberversicherung?", answer: "Ja, unbedingt! KIS-Ausfälle sind existenzbedrohend. Standard-Versicherungen decken Klinik-spezifische Risiken nicht ab." },
      { question: "Was kostet Cyberversicherung für Rehakliniken?", answer: "180-800€/Monat für kleine Kliniken (<50 Betten), 950-3.500€/Monat für große Rehazentren." }
    ],
    priceRange: "180-3500",
    employeeRange: "20-250",
    practiceCount: "1.100+"
  }
};

export const getPracticeType = (slug: string): PracticeTypeData | undefined => {
  return practiceTypes[slug];
};

export const getAllPracticeTypes = (): PracticeTypeData[] => {
  return Object.values(practiceTypes);
};
